import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { ItemThumb } from '../world/ItemSprite';
import { itemPath } from '../../utils/worldFormat';
import { classItems, itemRequirementText, sectionLabel } from '../../utils/classBuilds';
import type { CharacterClass } from '../../types/progression';
import type { WorldData } from '../../types/world';

/** Clase → equipo compatible (05_item_classes) → requisitos documentados → ficha con su obtención. */
export function ClassEquipment({
  cls,
  w,
  loading
}: {
  cls: CharacterClass;
  w?: WorldData;
  loading: boolean;
}) {
  const data = useMemo(() => w ? classItems(w, cls) : undefined, [w, cls]);
  const groups = useMemo(() => {
    const map = new Map<number, NonNullable<typeof data>['items']>();
    data?.items.forEach(ci => {
      if (ci.section === 15) return; // los pergaminos y libros se muestran en Habilidades
      const list = map.get(ci.section) ?? [];
      list.push(ci);
      map.set(ci.section, list);
    });
    return Array.from(map.entries());
  }, [data]);
  const [open, setOpen] = useState<number | null>(null);
  if (loading) return <p className="text-sm text-gray-500" data-id="element-333" data-mp="35eqeyu4njppe">Cargando datos del servidor…</p>;
  if (!data || !data.ids.length) return <p className="text-sm text-gray-500" data-id="element-334" data-mp="30fdq63u808k6">No se encontró esta clase entre las clases activas de la exportación.</p>;
  if (!groups.length) return <p className="text-sm text-gray-500" data-id="element-335" data-mp="3t6nqsl83qhqi">No hay equipo compatible documentado.</p>;
  return <div data-id="element-336" data-mp="3o6svm5han5zn">
      <ul className="divide-y divide-white/10 border-y border-white/10" data-id="element-337" data-mp="3o8dto52th729">
        {groups.map(([section, list]) => {
        const isOpen = open === section;
        return <li key={section} data-id="element-338" data-mp="3f75wxrfy8eaa">
              <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : section)} className="flex min-h-[48px] w-full items-center justify-between gap-3 py-3 text-left" data-id="element-339" data-mp="3f5usdpeoj2i4">
                <span className="font-semibold text-white" data-id="element-340" data-mp="258d7zgzdueyl">
                  {sectionLabel(section)} <span className="font-normal text-gray-500" data-id="element-341" data-mp="2a89b7e09a2d3">{list.length}</span>
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-342" data-mp="1w8hrqxqyztrp" />
              </button>
              {isOpen && <ul className="grid gap-x-6 gap-y-2 pb-4 sm:grid-cols-2" data-id="element-343" data-mp="1mhve392tddbn">
                  {list.map(ci => <li key={ci.id} className="flex items-center gap-3" data-id="element-344" data-mp="1r9na3z4qob96">
                      <ItemThumb id={ci.id} name={ci.name} size="thumb" data-id="element-345" data-mp="1ckhaia1zoj0r" />
                      <span className="min-w-0" data-id="element-346" data-mp="1hj225l36dudl">
                        <Link to={itemPath(ci.id)} className="block truncate text-sm font-semibold text-white hover:text-[#D4AF37]" data-id="element-347" data-mp="1hjtxqvwrvs3v">
                          {ci.name}
                        </Link>
                        <span className="block text-xs text-gray-400" data-id="element-348" data-mp="18h2kqro90oa4">{itemRequirementText(ci.item)}</span>
                      </span>
                    </li>)}
                </ul>}
            </li>;
      })}
      </ul>
      <p className="mt-2 text-xs text-gray-500" data-id="element-349" data-mp="xx0k3ap34waf">
        Compatibilidad según la configuración de Vanguard (05_item_classes). Requisitos de fuerza, agilidad y comando: no incluidos en la exportación.
      </p>
    </div>;
}